import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PaginationComponent from "../../../components/Pagination/Pagination";
import TableBackground from "../../../components/TableBackground";
import EmptyState from "../../../components/EmptyState";

import { debounce } from "lodash";
import SearchComponent from "../../../components/Search";
import moment from "moment";
import { getAgentUssdSessions } from "../../../redux/Agents/agentsAction";

const UssdSessionsTable = () => {
  const [status, setStatus] = useState("all");
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const { id } = useParams();

  const {
    agentUssdSessionsData,
    agentUssdSessionsTotal,
    agentUssdSessionsLoading,
  } = useSelector((state) => state.agents);

  const onPageChange = (val) => {
    const { page } = val;
    setCurrentPage(page);

    dispatch(
      getAgentUssdSessions({
        id: id,
        page: currentPage,
        search: page,
        status: status,
      })
    );
  };

  const handleSearch = useMemo(
    () =>
      debounce((val) => {
        //  api to dispatch and make the actions for search
        dispatch(
          getAgentUssdSessions({
            id: id,
            page: currentPage,
            search: val,
            status: status,
          })
        );
      }, 750),
    [dispatch, status, currentPage, id]
  );

  const handleChange = useCallback(
    (e) => {
      let val = e.target.value;
      setSearchValue(val);
      handleSearch(val);
    },
    [handleSearch]
  );

  const handleStatusChange = (tabStatus) => {
    setStatus(tabStatus);
    dispatch(
      getAgentUssdSessions({
        id: id,
        page: currentPage,
        search: searchValue,
        status: tabStatus,
      })
    );
  };

  useEffect(() => {
    dispatch(
      getAgentUssdSessions({
        id: id,
        page: currentPage,
        search: searchValue,
        status: status,
      })
    );
  }, [dispatch, status, id, currentPage, searchValue]);

  // const navigate = useNavigate()
  return (
    <div>
      <TableBackground>
        {/* SEARCH AND FILTER SECTION */}
        <section className="flex gap-4  pt-4">
          <div className="w-full relative px-4">
            <SearchComponent
              placeholder="Search..."
              searchValue={searchValue}
              handleChange={handleChange}
            />
          </div>
        </section>

        {/* TABS SECTION */}
        <section>
          <div className="flex gap-4 mt-4 mb-5 cursor-pointer px-4">
            <div
              className={
                status === "all"
                  ? "flex text-primary border-b-primary border-b-[2px] pb-[4px]"
                  : "flex text-[#96A397] pb-[4px]"
              }
              onClick={() => handleStatusChange("all")}
            >
              <div className="flex">
                <p className=" leading-[18px] text-sm font-semibold pr-[4px]">
                  All
                </p>
                <div
                  className={
                    status === "all"
                      ? "bg-[#DBFFDF] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                      : "bg-[#ECEEEC] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                  }
                >
                  <p>{status === "all" ? agentUssdSessionsTotal : "0"}</p>
                </div>
              </div>
            </div>

            <div
              className={
                status === "Complete"
                  ? "flex text-primary border-b-primary border-b-[2px] pb-[4px]"
                  : "flex text-[#96A397] pb-[4px]"
              }
              onClick={() => handleStatusChange("Complete")}
            >
              <div className="flex">
                <p className=" leading-[18px] text-sm font-semibold pr-[4px]">
                  Complete
                </p>
                <div
                  className={
                    status === "Complete"
                      ? "bg-[#DBFFDF] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                      : "bg-[#ECEEEC] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                  }
                >
                  <p>{status === "Complete" ? agentUssdSessionsTotal : "0"}</p>
                </div>
              </div>
            </div>

            <div
              className={
                status === "Incomplete"
                  ? "flex text-primary border-b-primary border-b-[2px] pb-[4px]"
                  : "flex text-[#96A397] pb-[4px]"
              }
              onClick={() => handleStatusChange("Incomplete")}
            >
              <div className="flex">
                <p className=" leading-[18px] text-sm font-semibold pr-[4px]">
                  Incomplete
                </p>
                <div
                  className={
                    status === "Incomplete"
                      ? "bg-[#DBFFDF] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                      : "bg-[#ECEEEC] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                  }
                >
                  <p>
                    {status === "Incomplete" ? agentUssdSessionsTotal : "0"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pt-[5px]">
          {agentUssdSessionsLoading ? (
            <div className="animate-pulse w-full mt-2 mb-4 pb-4 px-4">
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full"></div>
            </div>
          ) : (
            <>
              {agentUssdSessionsData.length === 0 ? (
                <div>
                  <EmptyState text="There's no data for this filter yet" />
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th scope="col" className="px-4 pb-2">
                        Activity
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Session Duration
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Previous Text{" "}
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Date
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Time
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Session Status
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Session Id
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentUssdSessionsData.map((data, index) => {
                      return (
                        <tr
                          key={index}
                          className="relative cursor-pointer hover:bg-[#edf7ee]"
                          // onClick={() => setIsClickTable(!isClickTable)}
                        >
                          <td className="px-4">
                            {data.text_activity || "N/A"}
                          </td>
                          <td className="px-4">N/A</td>
                          <td className="px-4">{data.prev_text || "N/A"}</td>
                          <td className="px-4">
                            {moment(data.created).format("ll")}
                          </td>
                          <td className="px-4">
                            {moment(data.created).format("LT")}
                          </td>
                          <td className="px-4">
                            <div
                              className={
                                data.session_status === "Incomplete"
                                  ? "error-badge"
                                  : "success-badge"
                              }
                            >
                              <p>{data.session_status || "N/A"}</p>
                            </div>
                          </td>
                          <td className="px-4">
                            {" "}
                            <div className="break-words w-[150px]">
                              <p>{data.session_id || "N/A"}</p>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}

          {!agentUssdSessionsLoading && agentUssdSessionsData.length > 0 ? (
            <div className="mt-1 pl-[-16px]" style={{ paddingLeft: "-16px" }}>
              <PaginationComponent
                page={currentPage}
                onPageChange={(page) => onPageChange({ page })}
                pageSize={10}
                totalItems={agentUssdSessionsTotal}
              />
            </div>
          ) : (
            ""
          )}
        </section>
      </TableBackground>
    </div>
  );
};

export default UssdSessionsTable;
